/**
 * formSubject.js
 *
 * Helpers for building and interpreting dynamic-form "subjects" — the person a
 * form response belongs to. A subject is a plain object:
 *
 *   {
 *     source: 'paua' | 'tk_person' | 'anonymous' | 'legacy_temporary',
 *     id:     number | null,   // Paua client_id, tk_people id, or null
 *     name:   string,          // display name
 *     email:  string | null,
 *     phone:  string | null,
 *   }
 *
 * The 'anonymous' source has no id — it is the shared bucket for responses
 * logged with no client at all (e.g. anonymous XLSX imports).
 */

export const SUBJECT_SOURCE = {
    PAUA: 'paua',
    TK_PERSON: 'tk_person',
    ANONYMOUS: 'anonymous',
    LEGACY: 'legacy_temporary',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** True when a stored contact value is a usable email address. */
export function isValidEmail(value) {
    return typeof value === 'string' && EMAIL_PATTERN.test(value.trim());
}

/** Join name parts into a trimmed display name. */
const joinName = (...parts) => parts.filter(Boolean).join(' ').trim();

/** The display label used for a tk_people record with no name. */
export const ANONYMOUS_NAME = 'Anonymous';

/**
 * Display label for the anonymous bucket — responses logged with no client at
 * all (subject_source = 'anonymous'). Parenthesised to read as a synthetic
 * entry rather than a real person in dropdowns and client lists.
 */
export const ANONYMOUS_LABEL = '(Anonymous)';

/**
 * The shared subject for anonymous responses. There is exactly one anonymous
 * bucket per form (no id), so this is a constant shape rather than a per-record
 * build.
 */
export function anonymousSubject() {
    return {
        source: SUBJECT_SOURCE.ANONYMOUS,
        id: null,
        name: ANONYMOUS_LABEL,
        email: null,
        phone: null,
    };
}

/**
 * Display name for a tk_people record. A record with no first/last/preferred
 * name (e.g. an anonymous bulk import) renders as "Anonymous".
 */
export function tkPersonName(person) {
    if (!person) return '';
    return person.preferred_name || joinName(person.first_name, person.last_name) || ANONYMOUS_NAME;
}

/** Build a subject from a Paua client record. */
export function pauaSubject(client) {
    if (!client) return null;
    return {
        source: SUBJECT_SOURCE.PAUA,
        id: client.client_id ?? null,
        name: client.full_name
            || joinName(client.client_first_name || client.first_name, client.client_last_name || client.last_name)
            || '-',
        email: client.email ?? null,
        phone: client.mobile_phone ?? client.phone ?? null,
    };
}

/**
 * Sentinel "record" representing the anonymous bucket where a tk_people record
 * is otherwise expected (e.g. the Te Kohinga select's model value). It is not a
 * real tk_people row — `is_anonymous` flags it so `tkPersonSubject` resolves it
 * to the anonymous subject rather than a person.
 */
export const ANONYMOUS_TK_OPTION = { is_anonymous: true, name: ANONYMOUS_LABEL };

/** True for the anonymous sentinel used in place of a tk_people record. */
export function isAnonymousTkOption(person) {
    return !!person?.is_anonymous;
}

/** Build a subject from a tk_people record (or the anonymous sentinel). */
export function tkPersonSubject(person) {
    if (!person) return null;
    if (isAnonymousTkOption(person)) return anonymousSubject();
    return {
        source: SUBJECT_SOURCE.TK_PERSON,
        id: person.id ?? null,
        name: tkPersonName(person) || '-',
        email: person.email ?? null,
        phone: person.phone ?? null,
    };
}

/** Attach an optional Client Engagement id to an existing form subject. */
export function subjectWithClientEngagement(subject, clientEngagementId) {
    if (!subject || clientEngagementId == null) return subject;
    return {
        ...subject,
        clientEngagementId,
    };
}

/** Remove Client Engagement context while retaining the underlying person subject. */
export function subjectWithoutClientEngagement(subject) {
    if (!subject) return subject;
    return Object.fromEntries(
        Object.entries(subject).filter(([key]) => (
            key !== 'clientEngagementId' && key !== 'client_engagement_id'
        ))
    );
}

/**
 * Build a subject from a stored form response's metadata row (as returned by
 * the dynamic-forms history/response endpoints).
 */
export function subjectFromResponse(meta) {
    if (!meta) return null;
    const source = meta.subject_source
        || (meta.client_id != null ? SUBJECT_SOURCE.PAUA : SUBJECT_SOURCE.LEGACY);

    if (source === SUBJECT_SOURCE.PAUA) {
        return {
            source: SUBJECT_SOURCE.PAUA,
            id: meta.client_id ?? null,
            name: meta.client_full_name || '-',
            email: null,
            phone: null,
        };
    }
    if (source === SUBJECT_SOURCE.TK_PERSON) {
        return {
            source: SUBJECT_SOURCE.TK_PERSON,
            id: meta.tk_person_id ?? null,
            name: meta.temporary_client_name || ANONYMOUS_NAME,
            email: meta.temporary_client_email ?? null,
            phone: meta.temporary_client_phone ?? null,
        };
    }
    if (source === SUBJECT_SOURCE.ANONYMOUS) {
        return anonymousSubject();
    }
    return {
        source: SUBJECT_SOURCE.LEGACY,
        id: null,
        name: meta.temporary_client_name || '-',
        email: meta.temporary_client_email ?? null,
        phone: meta.temporary_client_phone ?? null,
    };
}

/** True when the subject is a Paua client. */
export function isPaua(subject) {
    return subject?.source === SUBJECT_SOURCE.PAUA;
}

/**
 * Resolve the subject source of a dashboard client-list row. Rows carry an
 * explicit `subject_source`; fall back to the presence of a tk_person_id.
 */
function clientRowSource(row) {
    if (!row) return null;
    if (row.subject_source) return row.subject_source;
    return row.tk_person_id != null && row.client_id == null
        ? SUBJECT_SOURCE.TK_PERSON
        : SUBJECT_SOURCE.PAUA;
}

/** True when a dashboard client-list row is a temporary (tk_people) client. */
export function isTemporaryClientRow(row) {
    return clientRowSource(row) === SUBJECT_SOURCE.TK_PERSON;
}

/** True when a dashboard client-list row is the synthetic anonymous bucket. */
export function isAnonymousRow(row) {
    return clientRowSource(row) === SUBJECT_SOURCE.ANONYMOUS;
}

/**
 * Stable key for a dashboard client-list row, unique across Paua, temporary,
 * and anonymous rows (only Paua rows carry a client_id; the anonymous bucket
 * carries neither id, so its key is constant).
 */
export function clientRowKey(row) {
    if (!row) return null;
    const source = clientRowSource(row);
    if (source === SUBJECT_SOURCE.ANONYMOUS) return `${SUBJECT_SOURCE.ANONYMOUS}:null`;
    const id = source === SUBJECT_SOURCE.TK_PERSON ? row.tk_person_id : row.client_id;
    return `${source}:${id}`;
}

/**
 * Build a form subject from a dashboard client-list row, which may be a Paua
 * client, a temporary (tk_people) client, or the synthetic anonymous bucket.
 */
export function subjectFromClient(row) {
    if (!row) return null;
    const source = clientRowSource(row);
    if (source === SUBJECT_SOURCE.ANONYMOUS) {
        return anonymousSubject();
    }
    if (source === SUBJECT_SOURCE.TK_PERSON) {
        return {
            source: SUBJECT_SOURCE.TK_PERSON,
            id: row.tk_person_id ?? null,
            name: row.full_name || tkPersonName(row) || '-',
            email: row.email ?? null,
            phone: row.phone ?? null,
        };
    }
    return pauaSubject(row);
}

/** True when the subject identifies one specific person (Paua or tk_person). */
export function isPersonScoped(subject) {
    return !!subject
        && (subject.source === SUBJECT_SOURCE.PAUA || subject.source === SUBJECT_SOURCE.TK_PERSON)
        && subject.id != null;
}

/** True when the subject is the anonymous bucket (no specific person). */
export function isAnonymous(subject) {
    return subject?.source === SUBJECT_SOURCE.ANONYMOUS;
}

/**
 * True when a subject can have its response history loaded: either a specific
 * person, or the anonymous bucket (which has history but no id).
 */
export function hasLoadableHistory(subject) {
    return isPersonScoped(subject) || isAnonymous(subject);
}
